import { useEffect } from "react";
import { useLocation } from "react-router";
import { matchRoutes } from "react-router-config";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { updateModuleName, setMetaIndex } from "@App/appReducer";
import {
  persistEngageAttributionFromUrl,
  registerPostHogSuperProperties,
  setLastSearchedRegionNameForPostHog,
  setLastVisitedPropertyNameForPostHog,
  setRouteContextForPostHog,
  trackRoutePageView,
} from "@Utils/posthog";
import { APP_PAGE } from "@Utils/webApputils/analyticEvents";

// Pages that handle their own page_viewed event (e.g. AB test variants with different screen names)
const SELF_TRACKED_PAGES = [APP_PAGE.SCHOLARSHIP];

const DataLayout = (props: any) => {
  const location = useLocation();
  const dispatch = useDispatch();

  useEffect(() => {
    const { routes } = props;
    const routeData = matchRoutes(routes, location.pathname).reduce(
      (res: any, { route, match }: any) => {
        if (match.isExact) {
          return { route, match };
        }
        return res;
      },
      null,
    );

    persistEngageAttributionFromUrl();
    // Record the route's property / region slug first — registerPostHogSuperProperties
    // reads it back onto the person on the same pass.
    setRouteContextForPostHog(routeData?.route?.moduleName, routeData?.match?.params);
    registerPostHogSuperProperties().catch(() => undefined);

    // Seed meta.index from the route's `indexable` flag on CSR navigations. Use setMetaIndex
    // (merge) — never getMetaSuccess({ index }) which would wipe title/description/canonical.
    // Page loadData/fetch that calls getMetaSuccess afterwards still wins with a full replace.
    if (typeof routeData?.route?.indexable === "boolean") {
      dispatch(setMetaIndex(routeData.route.indexable));
    }

    const moduleName = routeData?.route?.moduleName;
    if (moduleName) {
      // @ts-ignore
      window.moduleName = moduleName;
      dispatch(updateModuleName(moduleName));
      if (!SELF_TRACKED_PAGES.includes(moduleName)) {
        trackRoutePageView(moduleName);
      }
    }

    if (routeData?.route?.prefetch) {
      setTimeout(() => {
        routeData?.route?.prefetch?.forEach((it: any) => {
          it.preload();
        });
      }, 1000);
    }
  }, [location.pathname]);

  // Display names arrive with the fetched data, not the route. Recorded here
  // rather than in PropertyTopSection, which often does not reach its write:
  // only 7 of 298 app users carrying a property slug also had its name.
  const { propertyName, regionName } = useSelector(
    ({ propertyPageData, searchPageData }: any) => ({
      propertyName: propertyPageData?.data?.name,
      regionName: searchPageData?.region?.name,
    }),
    shallowEqual,
  );

  useEffect(() => {
    if (!propertyName && !regionName) return;
    setLastVisitedPropertyNameForPostHog(propertyName);
    setLastSearchedRegionNameForPostHog(regionName);
    registerPostHogSuperProperties().catch(() => undefined);
  }, [propertyName, regionName]);

  return <></>;
};

export default DataLayout;
